import type {
  AnyContractProcedure,
  InferContractRouterInputs,
  InferContractRouterOutputs,
} from "@orpc/contract";
import { eventIterator, oc } from "@orpc/contract";
import * as z from "zod";
import { AdminHostSchema, AdminLicenseSchema, adminContract } from "./admin-contract.js";
import {
  AgentImportCreateInput,
  AgentImportPlanSchema,
  AgentImportPreviewInput,
} from "./agent-import.js";
import { AgentImportSetupStateInput, AgentImportSetupStateSchema } from "./agent-import-setup.js";
import { AiConsentQuerySchema, AiConsentStatusSchema } from "./ai-consent.js";
import { AnalyticsScreenInput } from "./analytics.js";
import {
  ARTIFACT_DESCRIPTION_MAX_LENGTH,
  ARTIFACT_NAME_MAX_LENGTH,
  ATTACHMENT_COUNT_CEILING,
} from "./attachments.js";
import {
  BillingInvoiceSchema,
  BillingOverviewSchema,
  ChangePlanInputSchema,
  ChangePlanResultSchema,
  StartTrialInputSchema,
  SupportTicketDetailSchema,
  SupportTicketSchema,
} from "./billing-panel.js";
import {
  BoardEventSchema,
  BoardSchema,
  CommentTicketInput,
  CreateTicketInput,
  GetTicketInput,
  ListTicketsInput,
  ListTicketsOutput,
  MoveTicketInput,
  RenameBoardInput,
  TicketCommentSchema,
  TicketSchema,
  UpdateTicketInput,
} from "./board.js";
import {
  BotSecretMetadata,
  BotSecretPutInput,
  LOGIN_FIELD_LIMIT,
  LoginFieldInput,
  StoredBotSecretName,
  VaultPutInput,
  VaultRevealed,
  VaultRevealInput,
} from "./bot-secrets.js";
import {
  CardChargeSchema,
  CardTokenFieldsSchema,
  PaymentAmountCentsSchema,
  PaymentConfigSchema,
  PaymentInvoiceSchema,
  PaymentSubscriptionSchema,
  PixChargeSchema,
  TaxIdTypeSchema,
} from "./charges.js";
import {
  AccountItemSchema,
  ActionApprovalRuleSchema,
  ActionAutoReviewSettingsSchema,
  AgentSecretInputSchema,
  AgentSecretSchema,
  AgentSkillCatalogEntrySchema,
  AgentSkillSchema,
  AppBootstrapSchema,
  ApprovalModeSchema,
  ApprovalRulePreviewSchema,
  ArtifactSchema,
  ArtifactVersionSchema,
  ArtifactWithContentSchema,
  AvailabilityKindSchema,
  AvatarStyleSchema,
  BillingClientSchema,
  BillingStatusSchema,
  BotMcpServerSchema,
  BotSchema,
  BotSectionName,
  BotSectionSchema,
  CapabilityInstallSchema,
  CONNECTION_LABEL_MAX_LENGTH,
  ComputerModeSchema,
  ComputerReleaseReasonSchema,
  ComputerStatusSchema,
  ComputerUpdateSchema,
  ConnectionCatalogItemSchema,
  ConnectionSchema,
  CreateAgentSkillInput,
  CreateBotInput,
  CreateGroupInput,
  CreateRoutineInput,
  CreateScratchpadItemInput,
  DeploymentSettingsSchema,
  ExportManifestSchema,
  ExternalConversationPolicySchema,
  GroupDetailSchema,
  GroupSchema,
  IntegrationCatalogResultSchema,
  McpServerConfigInput,
  McpServerSchema,
  MemoryDocumentSchema,
  MemoryScopeSchema,
  MeSchema,
  MessagingAgentConnectionSchema,
  MessagingChannelMembershipSchema,
  MessagingInstallationsStatusSchema,
  MessagingLinkedIdentitySchema,
  MessagingStatusSchema,
  ModelBackupChoiceSchema,
  ModelBackupListSchema,
  ModelCatalogEntrySchema,
  ModelConnectInputSchema,
  ModelConnectionLabelSchema,
  ModelConnectionTargetSchema,
  ModelCredentialSchema,
  ModelCreditAmountSchema,
  ModelOAuthBeginSchema,
  OPENAI_COMPATIBLE_PROVIDER_ID,
  PluginMarketplaceCatalogSchema,
  PluginPartSchema,
  PluginPreviewSchema,
  PluginSubmissionReviewSchema,
  PluginSubmissionSchema,
  PluginSubmissionStatusSchema,
  REPLY_QUOTE_MAX_LENGTH,
  ReorderBotsInput,
  ROUTINE_MENTION_BATCH_MAX,
  RoutineSchema,
  ScratchpadItemSchema,
  ScratchpadItemStatusSchema,
  ServerUpdateCheckSchema,
  ServerUpdateRequestSchema,
  ServerUpdateRunSchema,
  ServerUpdateStatusSchema,
  SignupSurveySchema,
  SkillPlaybookSchema,
  SpaceAvailabilitySchema,
  SpaceMemoryConfigSchema,
  SpaceNavigationSchema,
  SpaceSchema,
  TaughtSkillSchema,
  TeachRecordingEventSchema,
  ThinkingLevelSchema,
  ThreadMessagePageSchema,
  ThreadSnapshotSchema,
  TimeZoneSchema,
  UpdateAgentSkillInput,
  UpdateBotInput,
  UpdateExternalConversationPolicyInput,
  UpdateGroupInput,
  UsageRecordSchema,
  UsageSummarySchema,
  VoiceCatalogEntrySchema,
  VoiceCredentialSchema,
  VoiceInfoSchema,
  VoiceStatusSchema,
} from "./domain.js";
import { ComputerCommandSchema, ProductEventSchema } from "./events.js";
import { Id, IsoDate } from "./ids.js";
import {
  InferencePolicyInputSchema,
  InferencePolicyScopeSchema,
  InferencePolicyViewSchema,
} from "./inference-routing.js";
import {
  IntegrationProviderConfigSchema,
  IntegrationSetupStateSchema,
} from "./integration-settings.js";
import {
  BillingPreflightSchema,
  MeterDimensionSchema,
  MeterQuotaSchema,
  MeterRequirementSchema,
  MeterUsageBreakdownSchema,
  MeteringSummarySchema,
} from "./metering.js";
import { ModelHeadersSchema } from "./model-headers.js";
import { ModelRoleSchema, ModelRoleUpdateSchema, ModelRoleViewSchema } from "./model-roles.js";
import { PaymentWalletStatusSchema } from "./payments.js";
import { LicenseStatusSchema, PlanCatalogSchema, PlanIdSchema, PlanStateSchema } from "./plans.js";
import { MessageReactionSchema } from "./reactions.js";
import { RoutineWatchSchema } from "./routine-watch.js";
import {
  ROUTINE_HISTORY_PAGE_SIZE,
  RoutineHistorySchema,
  RoutineRunCursorSchema,
  RunsListOutputSchema,
} from "./runs.js";
import {
  ActivityListInputSchema,
  ActivityListSchema,
  AlertAcknowledgeInputSchema,
  AlertDeleteInputSchema,
  ActivityClearInputSchema,
  AlertSchema,
  AlertsListInputSchema,
  ChildControlsSchema,
  ChildMeSchema,
  ChildProfileSchema,
  ChildSessionSchema,
  ControlsGetInputSchema,
  ControlsUpdateInputSchema,
  HomeworkPhotoDeleteInputSchema,
  HomeworkPhotoListInputSchema,
  HomeworkPhotoListSchema,
  HomeworkResultSchema,
  HomeworkUploadInputSchema,
  KidsPinResultSchema,
  KidsSummarySchema,
  PairingCodeResultSchema,
  PairingCreateInputSchema,
  PairingRedeemInputSchema,
  PinResetInputSchema,
  PinSetInputSchema,
  PinVerifyInputSchema,
  ProfileArchiveInputSchema,
  ProfileCreateInputSchema,
  ProfileDeleteInputSchema,
  ProfileUpdateInputSchema,
  ReportGetInputSchema,
  ReportListInputSchema,
  ReportDeleteInputSchema,
  ReportListSchema,
  ReportSummarySchema,
  ContestInputSchema,
  ContestParentInputSchema,
  ContestReceiptSchema,
  ReportChannelCreateInputSchema,
  ReportChannelItemSchema,
  ReportChannelListInputSchema,
  ReportChannelListSchema,
  ReportChannelParentCreateInputSchema,
  ReportChannelReceiptSchema,
  ReportChannelResolveInputSchema,
  SessionExitInputSchema,
  SessionStartInputSchema,
} from "./kids.js";

import { SearchQueryOutputSchema } from "./search.js";
import { SecurityEventListInputSchema, SecurityEventSchema } from "./security-events.js";
import { SettingsScopeSchema, SpaceSettingSchema, settingsScopeInput } from "./settings-scope.js";

const botId = z.object({ botId: Id });
const groupId = z.object({ groupId: Id });

const threadTarget = z
  .object({
    botId: Id.optional(),
    groupId: Id.optional(),
  })
  .superRefine((input, ctx) => {
    const hasBot = Boolean(input.botId);
    const hasGroup = Boolean(input.groupId);
    if (hasBot === hasGroup) {
      ctx.addIssue({
        code: "custom",
        message: "Provide exactly one of botId or groupId",
        path: ["botId"],
      });
    }
  });

const structuredMentionTarget = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("bot"), id: Id }),
  z.object({ kind: z.literal("group"), id: Id }),
  z.object({ kind: z.literal("routine"), id: Id }),
  z.object({ kind: z.literal("connector"), id: Id }),
]);

const threadSendFields = threadTarget.safeExtend({
  imageHandling: z.enum(["confirm", "file"]).optional(),
  text: z.string().optional(),
  artifactIds: z.array(Id).max(ATTACHMENT_COUNT_CEILING).optional(),
  /** Bare bot ids (legacy) or typed mention chips from the composer. */
  mentions: z
    .array(z.union([Id, structuredMentionTarget]))
    .max(64)
    .optional(),
  replyToMessageId: Id.optional(),
  replyQuote: z.string().trim().min(1).max(REPLY_QUOTE_MAX_LENGTH).optional(),
  clientNonce: z.string().min(1).max(200).optional(),
});

const threadSendInput = threadSendFields.superRefine((input, ctx) => {
  const text = input.text?.trim() ?? "";
  const artifactIds = input.artifactIds ?? [];
  if (!text && artifactIds.length === 0) {
    ctx.addIssue({
      code: "custom",
      message: "Provide text or at least one attachment",
      path: ["text"],
    });
  }
  if (input.replyQuote && !input.replyToMessageId) {
    ctx.addIssue({
      code: "custom",
      message: "replyQuote requires replyToMessageId",
      path: ["replyQuote"],
    });
  }
});

const threadSendOutput = z.object({
  taskId: Id,
  runId: Id,
  seq: z.number().int(),
  runIds: z.array(Id).optional(),
});

/** Without text or attachments, only the routines run and no message is saved. */
const threadSendWithRoutinesInput = threadSendFields.safeExtend({
  clientNonce: z.string().min(1).max(200),
  routineIds: z.array(Id).min(1).max(64),
});

const spaceName = z.string().trim().min(1).max(60);

function adminProc<I extends z.ZodType, O extends z.ZodType>(definition: { input: I; output: O }) {
  return oc.input(definition.input).output(definition.output);
}

export const appContract = {
  analytics: { screen: oc.input(AnalyticsScreenInput).output(z.object({ ok: z.literal(true) })) },
  aiConsent: {
    status: oc.input(AiConsentQuerySchema).output(AiConsentStatusSchema),
    allow: oc
      .input(
        z.object({
          scope: z.string(),
          version: z.string(),
          keys: z.array(z.string()).min(1).max(200),
          /** The settings copy the reviewed recipients came from, as in `status`. */
          settingsScope: SettingsScopeSchema.optional(),
        }),
      )
      .output(AiConsentStatusSchema),
    revoke: oc.input(z.object({ key: z.string().nullable() })).output(AiConsentStatusSchema),
  },
  health: oc.output(z.object({ ok: z.literal(true), version: z.string() })),
  me: oc.output(MeSchema),
  billing: {
    status: oc.output(BillingStatusSchema),
    /** Re-reads the subscription from the provider, e.g. right after a mobile checkout. */
    refresh: oc.output(BillingStatusSchema),
    selectFunding: oc
      .input(z.object({ funding: z.enum(["byok", "credits"]), client: BillingClientSchema }))
      /** `url` is the checkout to pay for the own-key plan before it has access. */
      .output(z.object({ ok: z.literal(true), url: z.string().url().optional() })),
    purchaseCredits: oc
      .input(
        z.object({
          amountCents: ModelCreditAmountSchema,
          monthly: z.boolean(),
          client: BillingClientSchema,
        }),
      )
      .output(z.object({ url: z.string().url() })),
    setMonthlyCredits: oc
      .input(z.object({ amountCents: ModelCreditAmountSchema }))
      .output(z.object({ ok: z.literal(true) })),
    checkout: oc
      .input(z.object({ client: BillingClientSchema }).optional())
      .output(z.object({ url: z.string().url() })),
    portal: oc
      .input(z.object({ client: BillingClientSchema }).optional())
      .output(z.object({ url: z.string().url() })),
    /** Records who invited this organization; ignored once it has subscribed. */
    claimReferral: oc
      .input(z.object({ code: z.string().min(1).max(64) }))
      .output(z.object({ ok: z.literal(true) })),
    /**
     * What each metered dimension has left. `metered` is false when the operator runs the
     * deployment without billing: nothing is consumed and nothing is refused.
     */
    quotas: oc.output(
      z.object({
        metered: z.boolean(),
        dimensions: z.array(MeterQuotaSchema),
      }),
    ),
    /**
     * The account menu's view of the package: the plan, when it renews, and how much of each
     * capped dimension is used. `worstPercent` is the highest of them.
     */
    usageSummary: oc.output(MeteringSummarySchema),
    /** Where the organization's metered work went, by dimension, space and teammate. */
    usage: oc
      .input(
        z.object({
          dimension: MeterDimensionSchema.optional(),
          from: IsoDate.optional(),
          to: IsoDate.optional(),
        }),
      )
      .output(MeterUsageBreakdownSchema),
    /**
     * Checks the remaining quota before expensive work starts. The answer carries the state a
     * frontend turns into a recharge prompt when the work cannot go ahead.
     */
    preflight: oc
      .input(
        z.object({
          botId: Id.optional(),
          requirements: z.array(MeterRequirementSchema).max(3),
        }),
      )
      .output(BillingPreflightSchema),
    /**
     * The plan the workspace is on, what it allows, and what it costs. The prices are the
     * deployment's own.
     */
    plan: oc.output(PlanStateSchema),
    /**
     * What a plan costs for a term, and what the workspace gets back for the plan it
     * leaves: the unused days of what it paid for.
     */
    planQuote: oc
      .input(
        z.object({
          planId: PlanIdSchema,
          interval: z.enum(["monthly", "annual"]).default("monthly"),
          seats: z.number().int().positive().optional(),
        }),
      )
      .output(
        z.object({
          planId: PlanIdSchema,
          interval: z.enum(["monthly", "annual"]),
          /** The price of the term, before the credit. */
          priceCents: z.number().int().nonnegative(),
          /** The unused days of the plan it leaves. */
          creditCents: z.number().int().nonnegative(),
          /** What is actually charged. */
          amountCents: z.number().int().nonnegative(),
          periodStart: IsoDate,
          periodEnd: IsoDate,
          /** The price without the promotion. Equals priceCents when none applies. */
          fullPriceCents: z.number().int().nonnegative().default(0),
          /** The quote uses the launch price. */
          promoApplied: z.boolean().default(false),
          /** Until when that price is held (subscription start + 90 days). */
          priceHeldUntil: IsoDate.nullable().default(null),
          /**
           * `upgrade` charges amountCents now (price minus creditCents of unused days);
           * `downgrade` charges nothing now and starts at `effectiveAt`; `new` is a first subscription.
           */
          change: z.enum(["new", "upgrade", "downgrade", "same"]).default("new"),
          effectiveAt: IsoDate.nullable().default(null),
        }),
      ),
    /**
     * The license of an installation. Null when the deployment has none.
     */
    license: oc.output(LicenseStatusSchema.nullable()),
    /** The sellable catalog, with launch prices on each plan. */
    plans: oc.output(PlanCatalogSchema),
    /** Plan, state, trial, promotion, payment method and what the next renewal charges. */
    overview: oc.output(BillingOverviewSchema),
    /** Plan invoices and top-ups with the provider's invoice, receipt and NFS-e links. */
    invoices: oc.output(z.array(BillingInvoiceSchema)),
    /** Upgrades charge the prorated difference now; downgrades start at the period end. */
    changePlan: oc.input(ChangePlanInputSchema).output(ChangePlanResultSchema),
    /** Ends the plan at the end of the paid period. */
    cancel: oc
      .input(z.object({ reason: z.string().trim().max(500).optional() }))
      .output(BillingOverviewSchema),
    /** Undoes a cancellation or a scheduled downgrade before the period ends. */
    reactivate: oc.output(BillingOverviewSchema),
    /** Withdraws a plan choice that was never paid: closes its checkout and any recurring charge. */
    cancelPending: oc.output(BillingOverviewSchema),
    /** A hosted page to change the card or payment method. */
    paymentMethodUrl: oc.output(z.object({ url: z.string().url() })),
    /** Starts the 7-day trial: fixed credit, no card, one per verified phone or CPF. */
    startTrial: oc.input(StartTrialInputSchema).output(BillingOverviewSchema),
  },
  /** Verifies a phone number for the account (trial eligibility). Needs capabilities.signup.phoneVerification. */
  phone: {
    /** Sends a 6-digit code by WhatsApp. Rate limited per account and number. */
    sendCode: oc
      .input(z.object({ phone: z.string().trim().min(8).max(20) }))
      .output(z.object({ ok: z.literal(true), retryAfterSeconds: z.number().int().nonnegative() })),
    /** Confirms the code. The verified number is then accepted by billing.startTrial. */
    verify: oc
      .input(
        z.object({ phone: z.string().trim().min(8).max(20), code: z.string().trim().length(6) }),
      )
      .output(z.object({ ok: z.literal(true) })),
  },
  support: {
    /** Contact links the deployment configured; null hides the link. */
    channels: oc.output(
      z.object({ whatsappUrl: z.string().url().nullable(), email: z.string().email().nullable() }),
    ),
    createTicket: oc
      .input(
        z.object({
          subject: z.string().trim().min(3).max(200),
          body: z.string().trim().min(1).max(10_000),
        }),
      )
      .output(SupportTicketDetailSchema),
    listTickets: oc.output(z.array(SupportTicketSchema)),
    getTicket: oc.input(z.object({ ticketId: Id })).output(SupportTicketDetailSchema),
    reply: oc
      .input(z.object({ ticketId: Id, body: z.string().trim().min(1).max(10_000) }))
      .output(SupportTicketDetailSchema),
  },
  /** Orbin operators only (PREA_ADMIN_EMAILS). Audited; separate from customer roles. */
  admin: {
    customers: {
      search: adminProc(adminContract.customers.search),
      get: adminProc(adminContract.customers.get),
      addNote: adminProc(adminContract.customers.addNote),
    },
    compCredit: adminProc(adminContract.compCredit),
    extendTrial: adminProc(adminContract.extendTrial),
    cancelAtPeriodEnd: adminProc(adminContract.cancelAtPeriodEnd),
    changePlan: adminProc(adminContract.changePlan),
    refund: adminProc(adminContract.refund),
    suspend: adminProc(adminContract.suspend),
    restore: adminProc(adminContract.restore),
    deleteAccount: adminProc(adminContract.deleteAccount),
    support: {
      list: adminProc(adminContract.support.list),
      get: adminProc(adminContract.support.get),
      reply: adminProc(adminContract.support.reply),
    },
    revenue: adminProc(adminContract.revenue),
    overCost: adminProc(adminContract.overCost),
    abuseFlags: adminProc(adminContract.abuseFlags),
    hostPool: oc.output(z.array(AdminHostSchema)),
    licenses: {
      list: oc.output(z.array(AdminLicenseSchema)),
      issue: adminProc(adminContract.licenses.issue),
      revoke: adminProc(adminContract.licenses.revoke),
    },
    models: {
      overview: oc.output(adminContract.models.overview.output),
      usage: oc.output(adminContract.models.usage.output),
      setPolicy: adminProc(adminContract.models.setPolicy),
      clearPolicy: adminProc(adminContract.models.clearPolicy),
      setPrice: adminProc(adminContract.models.setPrice),
      clearPrice: adminProc(adminContract.models.clearPrice),
    },
    audit: adminProc(adminContract.audit),
    waitlist: {
      list: adminProc(adminContract.waitlist.list),
      exportCsv: oc.output(adminContract.waitlist.exportCsv.output),
      notifyLaunch: adminProc(adminContract.waitlist.notifyLaunch),
    },
  },
  /** Brazilian payments: PIX charges, domestic cards and monthly subscriptions. */
  payments: {
    /**
     * What the deployment offers: the methods it takes, the provider's public key for the
     * card form, and the BRL price of each credit pack. Null when payments are not configured.
     */
    config: oc.output(PaymentConfigSchema.nullable()),
    /**
     * A single-use link to the card checkout page. It authenticates that page only, and it
     * hands the customer back to the app when the payment is done.
     */
    cardCheckoutUrl: oc
      .input(z.object({ amountCents: PaymentAmountCentsSchema, client: BillingClientSchema }))
      .output(z.object({ url: z.string().url() })),
    /** Opens the checkout the link stands for. The token is good once. */
    cardCheckout: oc.input(z.object({ token: z.string().min(20).max(200) })).output(
      z.object({
        amountCents: z.number().int().positive(),
        /** "mobile" when the link came from the app, so the page hands back to it. */
        client: z.literal("mobile").nullable(),
        config: PaymentConfigSchema,
      }),
    ),
    /** Creates a PIX charge and returns the QR code the payer scans. */
    createPixCharge: oc
      .input(
        z.object({
          amountCents: PaymentAmountCentsSchema,
          description: z.string().trim().max(200).optional(),
          /** Stable across retries of the same purchase, so a retry is the same charge. */
          clientNonce: z.string().min(1).max(200).optional(),
        }),
      )
      .output(PixChargeSchema),
    /** What happened to a charge. Safe to poll while the payer is at their bank app. */
    chargeStatus: oc.input(z.object({ chargeId: Id })).output(PixChargeSchema),
    /** The organization's recurring plan, when it has one. */
    subscription: oc.output(PaymentSubscriptionSchema.nullable()),
    /** Starts a monthly plan on a tokenized domestic card. */
    createSubscription: oc
      .input(
        CardTokenFieldsSchema.extend({
          amountCents: PaymentAmountCentsSchema,
          /** Stable across retries of the same purchase, so a retry is the same plan. */
          clientNonce: z.string().min(1).max(200).optional(),
        }),
      )
      .output(PaymentSubscriptionSchema),
    /**
     * Subscribes to a plan of the catalog. The recurring flow is the same one a card
     * subscription uses; the plan's price is what it charges.
     */
    createPlanSubscription: oc
      .input(
        CardTokenFieldsSchema.extend({
          planId: PlanIdSchema,
          /** How often the plan renews. The annual plan is paid once for the year. */
          interval: z.enum(["monthly", "annual"]).optional(),
          /** The seats the plan is priced for. */
          seats: z.number().int().positive().optional(),
          clientNonce: z.string().min(1).max(200).optional(),
        }),
      )
      .output(PaymentSubscriptionSchema),
    /**
     * A PIX charge for a plan. The plan is granted when the payer pays.
     */
    createPlanPixCharge: oc
      .input(
        z.object({
          planId: PlanIdSchema,
          interval: z.enum(["monthly", "annual"]).optional(),
          seats: z.number().int().positive().optional(),
          clientNonce: z.string().min(1).max(200).optional(),
        }),
      )
      .output(PixChargeSchema),
    /** Ends the plan at the end of the period it already paid for. */
    cancelSubscription: oc.output(z.object({ ok: z.literal(true) })),
    /** The monthly invoices of the plan. */
    invoices: oc.output(z.array(PaymentInvoiceSchema)),
    /** A card charge on a tokenized domestic card. */
    createCardCharge: oc
      .input(
        CardTokenFieldsSchema.extend({
          amountCents: PaymentAmountCentsSchema,
          installments: z.number().int().min(1).max(12).optional(),
          /** Stable across retries of the same purchase, so a retry is the same charge. */
          clientNonce: z.string().min(1).max(200).optional(),
        }),
      )
      .output(CardChargeSchema),
    /**
     * Stores the payer's Brazilian tax id (CPF or CNPJ) for the providers that require one.
     * It is sealed at rest and never read back to the client.
     */
    setIdentification: oc
      .input(z.object({ taxId: z.string().trim().min(11).max(18) }))
      .output(z.object({ ok: z.literal(true), type: TaxIdTypeSchema })),
  },
  preferences: {
    update: oc
      .input(
        z
          .object({
            avatarStyle: AvatarStyleSchema.optional(),
            timezone: TimeZoneSchema.nullable().optional(),
            /** Where this account's computers run. "this-mac" needs a paired desktop app. */
            computerHost: z.enum(["docker", "this-mac"]).nullable().optional(),
          })
          .refine(
            (input) =>
              input.avatarStyle !== undefined ||
              input.timezone !== undefined ||
              input.computerHost !== undefined,
            { message: "Nothing to update" },
          ),
      )
      .output(MeSchema),
    /** Saves a detected timezone only while none is saved, so it never replaces a choice. */
    adoptTimezone: oc.input(z.object({ timezone: TimeZoneSchema })).output(MeSchema),
  },
  spaces: {
    list: oc.output(SpaceNavigationSchema),
    create: oc.input(z.object({ name: spaceName })).output(SpaceSchema),
    rename: oc
      .input(z.object({ spaceId: Id, name: spaceName }))
      .output(z.object({ id: Id, name: z.string() })),
    remove: oc
      .input(z.object({ spaceId: Id }))
      .output(z.object({ ok: z.literal(true), activeSpaceId: Id })),
  },
  bootstrap: oc.input(z.object({ botId: Id.optional() })).output(AppBootstrapSchema),
  deployment: {
    get: oc.output(DeploymentSettingsSchema),
    update: oc
      .input(
        z.object({
          signupsEnabled: z.boolean().optional(),
          signupAllowlist: z.array(z.string()).optional(),
          computerHost: z.enum(["docker", "this-mac"]).nullable().optional(),
        }),
      )
      .output(DeploymentSettingsSchema),
  },
  /** Deployment-owner view of the append-only security event log, newest first. */
  security: {
    events: oc.input(SecurityEventListInputSchema).output(z.array(SecurityEventSchema)),
  },
  /**
   * Deployment-owner product updates. When the Compose updater sidecar is reachable, these proxy
   * to its `/state` `/plan` `/apply` contract. Rollback stays on the sidecar for ops only and is
   * not exposed here. Never git-fetch from the API process.
   */
  updater: {
    status: oc.output(ServerUpdateStatusSchema),
    check: oc.input(ServerUpdateRequestSchema).output(ServerUpdateCheckSchema),
    apply: oc.input(ServerUpdateRequestSchema).output(ServerUpdateRunSchema),
  },
  models: {
    forRole: oc
      .input(z.object({ role: ModelRoleSchema, expectedUserId: Id.optional() }))
      .output(z.array(ModelCatalogEntrySchema)),
    role: oc
      .input(
        z.object({
          role: ModelRoleSchema,
          expectedUserId: Id.optional(),
          scope: SettingsScopeSchema.optional(),
        }),
      )
      .output(ModelRoleViewSchema),
    setRole: oc
      .input(
        ModelRoleUpdateSchema.extend({
          role: ModelRoleSchema,
          expectedUserId: Id.optional(),
          scope: SettingsScopeSchema.optional(),
        }),
      )
      .output(ModelRoleViewSchema),
    list: oc.output(z.array(ModelCatalogEntrySchema)),
    credentials: oc.output(z.array(ModelCredentialSchema)),
    /**
     * The routing policy that applies to the account or to the space, and where it comes
     * from: the space's own, the account's, or the operator default.
     */
    inferencePolicy: oc
      .input(z.object({ scope: InferencePolicyScopeSchema.default("space") }))
      .output(InferencePolicyViewSchema),
    /** Saves the account default or the space's exception. */
    setInferencePolicy: oc.input(InferencePolicyInputSchema).output(InferencePolicyViewSchema),
    /** Removes the scope's own policy, so the account default applies again. */
    clearInferencePolicy: oc
      .input(z.object({ scope: InferencePolicyScopeSchema.default("space") }))
      .output(InferencePolicyViewSchema),
    backups: oc.output(z.array(ModelBackupChoiceSchema)),
    setBackups: oc.input(ModelBackupListSchema).output(z.object({ ok: z.literal(true) })),
    connect: oc.input(ModelConnectInputSchema).output(ModelCredentialSchema),
    probeOpenAiCompatible: oc
      .input(
        z.object({
          baseUrl: z.string(),
          credentialId: z.string().optional(),
          headers: ModelHeadersSchema.optional(),
          apiKey: z.string().optional(),
        }),
      )
      .output(z.object({ models: z.array(z.string()) })),
    probeCatalog: oc
      .input(
        z.object({
          provider: z.string().trim().min(1),
          apiKey: z.string().trim().min(8),
        }),
      )
      .output(z.object({ models: z.array(z.string()) })),
    beginOAuth: oc
      .input(
        z.object({
          provider: z.string(),
          label: z.string().optional(),
          /** Signs an existing connection in again; null adds another, as connect does. */
          credentialId: ModelConnectionTargetSchema,
          modelId: z.string().optional(),
          thinkingLevel: ThinkingLevelSchema.nullable().optional(),
          makeDefault: z.boolean().optional(),
          scope: SettingsScopeSchema.optional(),
          /** No loopback listener (web, mobile): the provider shows a code to paste instead. */
          codePage: z.boolean().optional(),
        }),
      )
      .output(ModelOAuthBeginSchema),
    submitOAuthCode: oc
      .input(z.object({ loginId: z.string(), code: z.string().trim().min(1).max(8_192) }))
      .output(z.object({ ok: z.literal(true) })),
    completeOAuth: oc
      .input(z.object({ loginId: z.string() }))
      .output(
        z.discriminatedUnion("status", [
          z.object({ status: z.literal("pending") }),
          z.object({ status: z.literal("ready") }),
          z.object({ status: z.literal("error"), error: z.string() }),
        ]),
      ),
    finishOAuth: oc.input(z.object({ loginId: z.string() })).output(ModelCredentialSchema),
    cancelOAuth: oc
      .input(z.object({ loginId: z.string() }))
      .output(z.object({ ok: z.literal(true) })),
    setDefault: oc
      .input(
        z.object({
          provider: z.string(),
          modelId: z.string(),
          thinkingLevel: ThinkingLevelSchema.nullable().optional(),
        }),
      )
      .output(z.object({ ok: z.literal(true) })),
    rename: oc
      .input(z.object({ credentialId: Id, label: ModelConnectionLabelSchema }))
      .output(z.object({ ok: z.literal(true) })),
    /** Removes one connection, or every connection of the provider when older apps omit it. */
    disconnect: oc
      .input(z.object({ provider: z.string().trim().min(1), credentialId: Id.optional() }))
      .output(z.object({ ok: z.literal(true) })),
  },
  bots: {
    list: oc.output(z.array(BotSchema)),
    listArchived: oc.output(z.array(BotSchema)),
    get: oc.input(botId).output(BotSchema),
    create: oc.input(CreateBotInput).output(BotSchema),
    importPreview: oc.input(AgentImportPreviewInput).output(AgentImportPlanSchema),
    importCreate: oc.input(AgentImportCreateInput).output(BotSchema),
    importSetupState: oc.input(AgentImportSetupStateInput).output(AgentImportSetupStateSchema),
    duplicate: oc.input(botId).output(BotSchema),
    reorder: oc.input(ReorderBotsInput).output(z.object({ ok: z.literal(true) })),
    update: oc.input(UpdateBotInput).output(BotSchema),
    restoreInstructions: oc
      .input(
        z.object({
          botId: Id,
          versionId: Id,
          expectedInstructions: z.string(),
          undo: z.boolean().optional(),
        }),
      )
      .output(BotSchema),
    setComputer: oc.input(z.object({ botId: Id, mode: ComputerModeSchema })).output(BotSchema),
    archive: oc.input(botId).output(z.object({ ok: z.literal(true) })),
    restore: oc.input(botId).output(z.object({ ok: z.literal(true) })),
    remove: oc
      .input(z.object({ botId: Id, deleteMemories: z.boolean().default(false) }))
      .output(z.object({ ok: z.literal(true) })),
    rotateWebhookSecret: oc.input(botId).output(
      z.object({
        secret: z.string(),
        path: z.string(),
        webhookConfigured: z.literal(true),
      }),
    ),
  },
  groups: {
    create: oc.input(CreateGroupInput).output(GroupSchema),
    list: oc.output(z.array(GroupSchema)),
    listArchived: oc.output(z.array(GroupSchema)),
    get: oc.input(groupId).output(GroupDetailSchema),
    duplicate: oc.input(groupId).output(GroupSchema),
    update: oc.input(UpdateGroupInput).output(GroupSchema),
    archive: oc.input(groupId).output(z.object({ ok: z.literal(true) })),
    restore: oc.input(groupId).output(z.object({ ok: z.literal(true) })),
    remove: oc.input(groupId).output(z.object({ ok: z.literal(true) })),
  },
  botSections: {
    list: oc.output(z.array(BotSectionSchema)),
    create: oc.input(threadTarget.safeExtend({ name: BotSectionName })).output(BotSectionSchema),
    update: oc.input(z.object({ sectionId: Id, name: BotSectionName })).output(BotSectionSchema),
    remove: oc.input(z.object({ sectionId: Id })).output(
      z.object({
        id: Id,
        releasedBots: z.number().int().nonnegative(),
        releasedGroups: z.number().int().nonnegative(),
      }),
    ),
  },
  threads: {
    head: oc.input(threadTarget).output(
      z.object({
        threadId: Id,
        cursor: z.number().int().min(-1),
      }),
    ),
    get: oc.input(threadTarget).output(ThreadSnapshotSchema),
    messages: oc
      .input(
        threadTarget.safeExtend({
          before: z.number().int().nonnegative().optional(),
          includePeerRuns: z.boolean().optional(),
          includePeerReceipts: z.boolean().optional(),
          around: z
            .object({
              messageId: Id.optional(),
              seq: z.number().int().nonnegative().optional(),
            })
            .optional(),
        }),
      )
      .output(ThreadMessagePageSchema),
    subscribe: oc
      .input(threadTarget.safeExtend({ cursor: z.number().int().min(-1) }))
      .output(eventIterator(ProductEventSchema)),
    send: oc.input(threadSendInput).output(threadSendOutput),
    /**
     * A send that also test-runs the mentioned routines: the message and every run commit
     * together, keyed by the nonce. A separate procedure, because an API without it must
     * reject the call rather than strip the routines and send only the text.
     */
    sendWithRoutines: oc
      .input(threadSendWithRoutinesInput)
      .output(z.object({ routineRunIds: z.array(Id), send: threadSendOutput.optional() })),
    react: oc
      .input(
        threadTarget.safeExtend({
          messageId: Id,
          reaction: MessageReactionSchema,
          clientNonce: z.string().min(1).max(200),
        }),
      )
      .output(z.object({ ok: z.literal(true), runIds: z.array(Id).optional() })),
    stop: oc.input(threadTarget).output(z.object({ ok: z.literal(true) })),
    followUp: oc
      .input(
        threadTarget.safeExtend({
          text: z.string().min(1),
          /** Carries the call id, so a turn taken mid-run stays in the call's card. */
          clientNonce: z.string().min(1).max(200).optional(),
        }),
      )
      .output(z.object({ ok: z.literal(true) })),
    /** The client hung up: close the call card and let the bot finish what was asked on it. */
    endCall: oc
      // ":" separates the call id from the nonce suffix, so it can never appear inside one.
      .input(
        botId.safeExtend({
          callId: z
            .string()
            .min(1)
            .max(200)
            .regex(/^[^:]+$/),
          /** How long the call was connected, measured from pickup on the caller's client. */
          durationSeconds: z
            .number()
            .int()
            .nonnegative()
            .max(7 * 24 * 60 * 60)
            .optional(),
        }),
      )
      .output(z.object({ ok: z.literal(true) })),
    /** The user picked up the bot's call: returns what the bot opens with. */
    answerCall: oc
      .input(
        botId.safeExtend({
          callId: z
            .string()
            .min(1)
            .max(200)
            .regex(/^[^:]+$/),
        }),
      )
      .output(z.object({ opening: z.string(), reason: z.string() })),
    /** The user turned the bot's call down, asked for it later, or it rang out. */
    declineCall: oc
      .input(
        botId.safeExtend({
          callId: z
            .string()
            .min(1)
            .max(200)
            .regex(/^[^:]+$/),
          outcome: z.enum(["declined", "missed"]),
        }),
      )
      .output(z.object({ ok: z.literal(true) })),
    clear: oc.input(threadTarget).output(z.object({ ok: z.literal(true) })),
    answer: oc
      .input(
        threadTarget
          .safeExtend({
            runId: Id,
            messageId: Id,
            answer: z.string(),
            /** Carries the call id for an answer spoken during a call. */
            clientNonce: z.string().min(1).max(200).optional(),
            /** A login card's answer, one per requested field; `answer` is then empty. */
            fields: z.array(LoginFieldInput).min(1).max(LOGIN_FIELD_LIMIT).optional(),
            /** A login card's answer from apps that predate `fields`; `answer` carries its password. */
            username: z.string().min(1).max(512).optional(),
            /** Only for a login card: use the login for this run only instead of saving it. */
            remember: z.literal(false).optional(),
            /** Turns down a pending secret request; `answer` is then empty. */
            dismiss: z.literal(true).optional(),
          })
          .refine(
            (input) => input.remember === undefined || Boolean(input.fields ?? input.username),
            {
              message: "Only a website login can be used without saving it",
              path: ["remember"],
            },
          )
          .refine(
            (input) =>
              input.dismiss
                ? input.answer === "" && !input.username && !input.fields && !input.clientNonce
                : input.fields
                  ? input.answer === "" && !input.username
                  : input.answer.length > 0,
            "Send either an answer or a dismissal",
          ),
      )
      .output(z.object({ ok: z.literal(true) })),
    /** `unreadAfterSeq`: the seq read up to before this call, null when nothing was unread. */
    markRead: oc
      .input(threadTarget)
      .output(z.object({ ok: z.literal(true), unreadAfterSeq: z.number().int().nullable() })),
    /** With `messageId`, the chat reads as unread from that message: the next open puts New above it. */
    markUnread: oc
      .input(threadTarget.and(z.object({ messageId: Id.optional() })))
      .output(z.object({ ok: z.literal(true) })),
  },
  computer: {
    status: oc.input(botId).output(ComputerStatusSchema),
    boot: oc.input(botId).output(ComputerStatusSchema),
    stop: oc.input(botId).output(ComputerStatusSchema),
    recover: oc.input(botId).output(ComputerUpdateSchema),
    reset: oc.input(botId).output(ComputerStatusSchema),
    update: oc.input(botId).output(ComputerUpdateSchema),
    updates: oc.output(z.array(ComputerUpdateSchema)),
    releaseInterrupted: oc
      .input(z.object({ id: Id, workersStopped: z.literal(true) }))
      .output(z.object({ ok: z.literal(true) })),
    dismissUpdate: oc.input(z.object({ id: Id })).output(z.object({ ok: z.literal(true) })),
    takeover: oc.input(botId).output(z.object({ leaseId: Id, expiresAt: z.string() })),
    release: oc
      .input(
        z.object({
          botId: Id,
          reason: ComputerReleaseReasonSchema.optional(),
        }),
      )
      .output(z.object({ ok: z.literal(true) })),
    input: oc
      .input(
        z.object({
          botId: Id,
          kind: z.enum(["key", "pointer", "clipboard", "scroll"]),
          payload: z.record(z.string(), z.unknown()),
        }),
      )
      .output(z.object({ ok: z.literal(true) })),
    files: oc
      .input(z.object({ botId: Id, path: z.string().default("/") }))
      .output(
        z.array(z.object({ path: z.string(), kind: z.enum(["file", "dir"]), size: z.number() })),
      ),
    readFile: oc
      .input(z.object({ botId: Id, path: z.string() }))
      .output(z.object({ path: z.string(), content: z.string() })),
    downloadFile: oc
      .input(z.object({ botId: Id, path: z.string().min(1) }))
      .output(z.object({ path: z.string(), contentBase64: z.string() })),
    uploadFile: oc
      .input(
        z.object({
          botId: Id,
          path: z.string().min(1),
          contentBase64: z.string(),
        }),
      )
      .output(z.object({ ok: z.literal(true) })),
    terminalUrl: oc.input(botId).output(z.object({ url: z.string().nullable() })),
    commands: oc
      .input(botId)
      .output(z.array(ComputerCommandSchema.extend({ createdAt: z.string() }))),
    // A stopped or suspended computer is woken only when the request asks to resume it.
    screenUrl: oc
      .input(botId.extend({ resume: z.boolean().optional() }))
      .output(z.object({ url: z.string().nullable() })),
    heartbeat: oc.input(botId).output(z.object({ ok: z.literal(true) })),
  },
  memory: {
    list: oc
      .input(z.object({ botId: Id.optional(), scope: z.enum(["bot", "user"]).optional() }))
      .output(z.array(MemoryDocumentSchema)),
    update: oc
      .input(
        z.object({
          documentId: Id,
          content: z.string(),
          expectedRevision: z.number().int().positive().optional(),
        }),
      )
      .output(MemoryDocumentSchema),
    remove: oc.input(z.object({ documentId: Id })).output(z.object({ ok: z.literal(true) })),
    exportMarkdown: oc.input(z.object({ botId: Id.optional() })).output(z.string()),
    providerConfig: oc.output(SpaceMemoryConfigSchema.nullable()),
    connectProvider: oc
      .input(
        z.object({
          provider: z.string().min(1),
          settings: z.record(z.string(), z.string()),
          credentials: z.record(z.string(), z.string()),
          defaultMemoryScope: MemoryScopeSchema.default("isolated"),
        }),
      )
      .output(SpaceMemoryConfigSchema),
    setDefaultScope: oc
      .input(z.object({ defaultMemoryScope: MemoryScopeSchema }))
      .output(SpaceMemoryConfigSchema),
    disconnectProvider: oc.output(z.object({ ok: z.literal(true) })),
  },
  routines: {
    listForMentions: oc
      .input(z.object({ botIds: z.array(Id).min(1).max(ROUTINE_MENTION_BATCH_MAX) }))
      .output(z.array(z.object({ botId: Id, routines: z.array(RoutineSchema) }))),
    list: oc.input(botId).output(z.array(RoutineSchema)),
    history: oc
      .input(
        z.object({
          routineId: Id,
          before: RoutineRunCursorSchema.optional(),
          limit: z.number().int().min(1).max(ROUTINE_HISTORY_PAGE_SIZE).optional(),
        }),
      )
      .output(RoutineHistorySchema),
    create: oc.input(CreateRoutineInput).output(RoutineSchema),
    update: oc
      .input(
        z
          .object({
            routineId: Id,
            name: z.string().optional(),
            prompt: z.string().optional(),
            crons: z.array(z.string().min(1)).optional(),
            timezone: TimeZoneSchema.optional(),
            active: z.boolean().optional(),
            notify: z.boolean().optional(),
            webhookEnabled: z.boolean().optional(),
            githubEnabled: z.boolean().optional(),
            messageProvider: z
              .string()
              .min(1)
              .max(50)
              .regex(/^[a-z0-9._-]+$/i)
              .nullable()
              .optional(),
            /** Null clears the routine's own model and runs it on the bot's. */
            modelProvider: z.string().trim().min(1).max(80).nullable().optional(),
            modelId: z.string().trim().min(1).max(200).nullable().optional(),
            thinkingLevel: ThinkingLevelSchema.nullable().optional(),
            /** ISO datetime to arm a never-run one-shot. */
            runAt: IsoDate.optional(),
            /** Null removes the change trigger. */
            watch: RoutineWatchSchema.nullable().optional(),
          })
          .superRefine((value, ctx) => {
            if (
              value.crons &&
              value.crons.length === 0 &&
              value.webhookEnabled === false &&
              value.githubEnabled === false &&
              value.messageProvider === null &&
              value.watch === null
            ) {
              ctx.addIssue({
                code: "custom",
                message: "Add a schedule, webhook, GitHub, message, or change trigger",
                path: ["crons"],
              });
            }
          }),
      )
      .output(RoutineSchema),
    remove: oc.input(z.object({ routineId: Id })).output(z.object({ ok: z.literal(true) })),
    testRun: oc
      .input(
        z.object({
          routineId: Id,
          clientNonce: z.string().min(1).max(200).optional(),
        }),
      )
      .output(z.object({ runId: Id })),
  },
  scratchpad: {
    list: oc
      .input(
        z.object({
          botId: Id,
          status: ScratchpadItemStatusSchema.optional(),
          includeDone: z.boolean().optional(),
        }),
      )
      .output(z.array(ScratchpadItemSchema)),
    create: oc.input(CreateScratchpadItemInput).output(ScratchpadItemSchema),
    update: oc
      .input(
        z.object({
          itemId: Id,
          title: z.string().min(1).max(200).optional(),
          status: ScratchpadItemStatusSchema.optional(),
          notes: z.string().max(4_000).optional(),
        }),
      )
      .output(ScratchpadItemSchema),
    remove: oc.input(z.object({ itemId: Id })).output(z.object({ ok: z.literal(true) })),
  },
  boards: {
    list: oc.output(z.array(BoardSchema)),
    get: oc.output(BoardSchema),
    rename: oc.input(RenameBoardInput).output(BoardSchema),
    subscribe: oc.output(eventIterator(BoardEventSchema)),
  },
  tickets: {
    list: oc.input(ListTicketsInput).output(ListTicketsOutput),
    get: oc.input(GetTicketInput).output(TicketSchema),
    create: oc.input(CreateTicketInput).output(TicketSchema),
    update: oc.input(UpdateTicketInput).output(TicketSchema),
    move: oc.input(MoveTicketInput).output(TicketSchema),
    comment: oc.input(CommentTicketInput).output(TicketCommentSchema),
    comments: oc.input(z.object({ ticketId: Id })).output(z.array(TicketCommentSchema)),
  },
  skills: {
    list: oc.input(botId).output(z.array(TaughtSkillSchema)),
    get: oc.input(z.object({ skillId: Id })).output(TaughtSkillSchema),
    start: oc
      .input(z.object({ botId: Id, goal: z.string().min(1).max(4000) }))
      .output(TaughtSkillSchema),
    appendEvent: oc
      .input(z.object({ skillId: Id, event: TeachRecordingEventSchema }))
      .output(TaughtSkillSchema),
    snapshot: oc.input(z.object({ skillId: Id })).output(TaughtSkillSchema),
    stop: oc.input(z.object({ skillId: Id })).output(TaughtSkillSchema),
    updateDraft: oc
      .input(
        z.object({
          skillId: Id,
          name: z.string().optional(),
          playbook: SkillPlaybookSchema,
        }),
      )
      .output(TaughtSkillSchema),
    save: oc
      .input(z.object({ skillId: Id, name: z.string().optional() }))
      .output(TaughtSkillSchema),
    testRun: oc
      .input(z.object({ skillId: Id, prompt: z.string().optional() }))
      .output(z.object({ runId: Id })),
    remove: oc.input(z.object({ skillId: Id })).output(z.object({ ok: z.literal(true) })),
  },
  /** Claude Agent Skills (SKILL.md recipes) shared across assistants (not taught/demo skills). */
  agentSkills: {
    list: oc.output(z.array(AgentSkillCatalogEntrySchema)),
    get: oc
      .input(
        z
          .object({ skillId: Id.optional(), name: z.string().min(1).max(80).optional() })
          .superRefine((input, ctx) => {
            if (!input.skillId && !input.name?.trim()) {
              ctx.addIssue({
                code: "custom",
                message: "Provide skillId or name",
                path: ["skillId"],
              });
            }
          }),
      )
      .output(AgentSkillSchema),
    create: oc.input(CreateAgentSkillInput).output(AgentSkillSchema),
    update: oc.input(UpdateAgentSkillInput).output(AgentSkillSchema),
    remove: oc.input(z.object({ skillId: Id })).output(z.object({ ok: z.literal(true) })),
  },
  availability: {
    set: oc
      .input(z.object({ kind: AvailabilityKindSchema, id: Id }).and(SpaceAvailabilitySchema))
      .output(SpaceAvailabilitySchema),
    /** The user's own skills, MCP servers, plugins and tool sources across their spaces. */
    items: oc.output(z.array(AccountItemSchema)),
    /** Make exactly the listed items available in a space, keeping each one's other spaces. */
    chooseForSpace: oc
      .input(
        z.object({
          spaceId: Id,
          items: z.array(z.object({ kind: AvailabilityKindSchema, id: Id })).max(500),
        }),
      )
      .output(z.object({ ok: z.literal(true) })),
  },
  marketplace: {
    catalog: oc
      .input(z.object({ query: z.string().trim().max(200).default("") }))
      .output(PluginMarketplaceCatalogSchema),
    /** Admins only. */
    submissions: oc
      .input(z.object({ status: PluginSubmissionStatusSchema.default("pending") }))
      .output(z.array(PluginSubmissionSchema)),
    /** Admins only. */
    review: oc.input(PluginSubmissionReviewSchema).output(PluginSubmissionSchema),
  },
  capabilities: {
    list: oc.output(z.array(CapabilityInstallSchema)),
    catalogSearch: oc
      .input(
        z.object({
          query: z.string().trim().max(253).default(""),
          usePublicCatalog: z.boolean().default(false),
        }),
      )
      .output(
        z.object({
          enabled: z.boolean(),
          results: z.array(IntegrationCatalogResultSchema),
        }),
      ),
    install: oc
      .input(
        z.object({
          kind: z.enum(["skill", "plugin", "mcp", "api", "graphql"]),
          name: z.string().min(1).max(120),
          source: z.string().min(1).max(2048),
          config: z.record(z.string(), z.unknown()).default({}),
          credential: z.string().max(16_384).optional(),
          /** Required for plugins: confirms the exact previewed package content. */
          pluginDigest: z
            .string()
            .regex(/^sha256:[a-f0-9]{64}$/)
            .optional(),
          /** Plugins only: which parts to add; all supported parts when omitted. */
          pluginParts: z.array(PluginPartSchema).min(1).max(2).optional(),
        }),
      )
      .output(CapabilityInstallSchema),
    /** Add or remove parts of an installed plugin at its pinned version; no parts removes it. */
    pluginParts: oc
      .input(z.object({ id: Id, parts: z.array(PluginPartSchema).max(2) }))
      .output(z.object({ install: CapabilityInstallSchema.nullable() })),
    /** Fetch and parse a plugin repo without installing: shows the skills,
     * MCP endpoints, variables, and skipped parts before confirmation. */
    pluginPreview: oc
      .input(z.object({ source: z.string().min(1).max(2048) }))
      .output(PluginPreviewSchema),
    remove: oc.input(z.object({ id: Id })).output(z.object({ ok: z.literal(true) })),
  },
  mcp: {
    servers: {
      list: oc.output(z.array(McpServerSchema)),
      create: oc.input(McpServerConfigInput).output(McpServerSchema),
      update: oc
        .input(
          z.union([
            z.object({ id: Id, config: McpServerConfigInput }),
            z.object({ id: Id, secret: z.string().min(1).max(16384) }),
          ]),
        )
        .output(McpServerSchema),
      remove: oc.input(z.object({ id: Id })).output(z.object({ ok: z.literal(true) })),
      /** Launch the server once (installing a stdio package if needed), list its tools, close it. */
      test: oc.input(z.object({ id: Id })).output(McpServerSchema),
    },
    assignments: {
      list: oc.input(botId).output(z.array(BotMcpServerSchema)),
      all: oc.output(z.array(BotMcpServerSchema)),
      approve: oc
        .input(z.object({ botId: Id, serverId: Id, threadId: Id.optional() }))
        .output(BotMcpServerSchema),
      dismiss: oc
        .input(z.object({ botId: Id, serverId: Id, threadId: Id.optional() }))
        .output(z.object({ ok: z.literal(true) })),
      replace: oc
        .input(
          z.object({
            botId: Id,
            assignments: z.array(
              z.object({
                serverId: Id,
                allowAllTools: z.boolean().default(true),
                allowedTools: z.array(z.string().min(1).max(200)).max(500).default([]),
              }),
            ),
          }),
        )
        .output(z.array(BotMcpServerSchema)),
    },
    oauth: {
      begin: oc.input(z.object({ serverId: Id, redirectUri: z.string().url().optional() })).output(
        z.discriminatedUnion("status", [
          z.object({
            status: z.literal("authorization_required"),
            sessionId: Id,
            authorizationUrl: z.string().url(),
          }),
          z.object({
            status: z.enum(["already_connected", "authorization_not_requested"]),
          }),
        ]),
      ),
      complete: oc
        .input(z.object({ sessionId: Id, code: z.string().min(1), state: z.string().min(1) }))
        .output(z.object({ ok: z.literal(true) })),
      disconnect: oc.input(z.object({ serverId: Id })).output(z.object({ ok: z.literal(true) })),
    },
  },
  onboarding: {
    /** Record the optional signup survey; an empty submission marks it skipped. */
    signupSurvey: oc.input(SignupSurveySchema).output(z.object({ ok: z.literal(true) })),
    /** Seed the first-run greeting into the bot's thread (focus card is separate). */
    start: oc.input(z.object({ botId: Id })).output(z.object({ ok: z.literal(true) })),
    /** Post the focus choice card when the thread is still idle. */
    promptFocus: oc.input(z.object({ botId: Id })).output(z.object({ ok: z.literal(true) })),
    /** Answer the focus choice; posts the app cards. Does not rename the bot. */
    choose: oc
      .input(z.object({ botId: Id, optionId: z.string() }))
      .output(z.object({ ok: z.literal(true) })),
    /** Dismiss the unanswered focus card without choosing an option. */
    dismissFocus: oc.input(z.object({ botId: Id })).output(z.object({ ok: z.literal(true) })),
    /** Flip an app_connect card to connected after authorization completes. */
    appConnected: oc
      .input(
        z.object({
          botId: Id,
          threadId: Id.optional(),
          /** The card's own message, so it is found however far back it is. */
          messageId: Id.optional(),
          provider: z.string(),
          connectorId: z.string().default("composio"),
        }),
      )
      .output(z.object({ ok: z.literal(true) })),
  },
  integrationSetup: {
    get: oc.output(IntegrationSetupStateSchema),
    save: oc.input(IntegrationProviderConfigSchema).output(z.object({ ok: z.literal(true) })),
  },
  connections: {
    catalog: oc
      .input(z.object({ query: z.string().optional(), connectorId: z.string().optional() }))
      .output(z.array(ConnectionCatalogItemSchema)),
    list: oc.output(z.array(ConnectionSchema)),
    begin: oc
      .input(
        z.object({
          connectorId: z.string().default("composio"),
          provider: z.string(),
          displayName: z.string(),
        }),
      )
      .output(z.object({ connectionId: Id, authorizationUrl: z.string().nullable() })),
    complete: oc
      .input(z.object({ connectionId: Id, code: z.string().optional() }))
      .output(ConnectionSchema),
    rename: oc
      .input(
        z.object({
          connectionId: Id,
          displayName: z.string().trim().min(1).max(CONNECTION_LABEL_MAX_LENGTH),
        }),
      )
      .output(ConnectionSchema),
    revoke: oc.input(z.object({ connectionId: Id })).output(z.object({ ok: z.literal(true) })),
    /** Tools the connected provider exposes. Read-only; no per-tool allowlist yet. */
    tools: oc.input(z.object({ connectorId: z.string(), provider: z.string() })).output(
      z.array(
        z.object({
          name: z.string(),
          description: z.string(),
        }),
      ),
    ),
  },
  /** External messaging surface: link state, group channels, agent connections. */
  messaging: {
    installations: {
      list: oc.output(MessagingInstallationsStatusSchema),
      /** Without a bot, the workspace answers as the first bot in the user's default space. */
      start: oc.input(z.object({ botId: Id.optional() })).output(z.object({ url: z.string() })),
      complete: oc
        .input(z.object({ state: z.string().max(64), code: z.string().max(4096) }))
        .output(z.object({ ok: z.literal(true) })),
      remove: oc.input(z.object({ id: Id })).output(z.object({ ok: z.literal(true) })),
    },
    status: oc.output(MessagingStatusSchema),
    link: {
      /** Issue a short-lived code the user sends to the line from a chat app. */
      start: oc
        .input(z.object({ botId: Id }))
        .output(z.object({ code: z.string(), expiresAt: z.string() })),
    },
    identities: {
      setBot: oc
        .input(z.object({ identityId: Id, botId: Id }))
        .output(MessagingLinkedIdentitySchema),
      unlink: oc.input(z.object({ identityId: Id })).output(z.object({ ok: z.literal(true) })),
    },
    channels: {
      list: oc.output(z.array(MessagingChannelMembershipSchema)),
      // Addressed by membership, not channel: one user can have two linked
      // chat apps in the same group, and each answers for its own agent.
      respond: oc
        .input(z.object({ membershipId: Id, accept: z.boolean() }))
        .output(MessagingChannelMembershipSchema),
      leave: oc.input(z.object({ membershipId: Id })).output(z.object({ ok: z.literal(true) })),
    },
    connections: {
      list: oc.output(z.array(MessagingAgentConnectionSchema)),
      respond: oc
        .input(z.object({ connectionId: Id, accept: z.boolean() }))
        .output(MessagingAgentConnectionSchema),
      revoke: oc.input(z.object({ connectionId: Id })).output(z.object({ ok: z.literal(true) })),
    },
  },
  approvalRules: {
    list: oc.input(settingsScopeInput.optional()).output(z.array(ActionApprovalRuleSchema)),
    preview: oc
      .input(
        settingsScopeInput.extend({
          id: Id,
          effect: z.enum(["always_allow", "deny", "remove"]),
          sample: z.object({
            tools: z
              .array(
                z.object({
                  name: z.string().trim().min(1).max(200),
                  connectorKind: z.string().trim().min(1).max(200).optional(),
                  readOnly: z.boolean().optional(),
                }),
              )
              .min(1)
              .max(20),
            wrapper: z
              .object({
                name: z.string().trim().min(1).max(200),
                connectorKind: z.string().trim().min(1).max(200).optional(),
              })
              .optional(),
            site: z.string().trim().min(1).max(253).optional(),
          }),
        }),
      )
      .output(ApprovalRulePreviewSchema),
    set: oc
      .input(
        settingsScopeInput.extend({
          effect: z.enum(["always_allow", "deny"]),
          matchKind: z.enum(["tool", "connector", "site"]),
          matchValue: z.string().min(1),
        }),
      )
      .output(ActionApprovalRuleSchema),
    remove: oc
      .input(settingsScopeInput.extend({ id: Id }))
      .output(z.object({ ok: z.literal(true) })),
  },
  /**
   * Sites the signed-in user opens from chat links without the external-link prompt, in every
   * space. The server computes each site from a link; bot fetches never read this list.
   */
  trustedLinkSites: {
    list: oc.output(z.array(z.string())),
    trust: oc
      .input(z.object({ url: z.string().min(1).max(8192) }))
      .output(z.object({ site: z.string() })),
    remove: oc
      .input(z.object({ site: z.string().min(1).max(253) }))
      .output(z.object({ ok: z.literal(true) })),
  },
  autoReview: {
    get: oc.input(settingsScopeInput.optional()).output(ActionAutoReviewSettingsSchema),
    set: oc
      .input(
        settingsScopeInput.extend({
          mode: ApprovalModeSchema.optional(),
          enabled: z.boolean().optional(),
          skipApprovals: z.boolean().optional(),
        }),
      )
      .output(ActionAutoReviewSettingsSchema),
  },
  /** Which account settings the bound Space overrides for the member. */
  spaceSettings: {
    overrides: oc.output(z.array(SpaceSettingSchema)),
    /** Customizing copies the account's settings into the Space; ending it drops the copy. */
    setOverride: oc
      .input(z.object({ setting: SpaceSettingSchema, enabled: z.boolean() }))
      .output(z.array(SpaceSettingSchema)),
  },
  artifacts: {
    list: oc.input(botId).output(z.array(ArtifactSchema)),
    listSpace: oc
      .input(
        z.object({
          botId: Id.optional(),
          cursor: z.string().optional(),
          limit: z.number().int().min(1).max(60).optional(),
        }),
      )
      .output(
        z.object({
          items: z.array(ArtifactSchema.extend({ versionCount: z.number().int() })),
          nextCursor: z.string().nullable(),
        }),
      ),
    listVersions: oc.input(z.object({ familyId: Id })).output(z.array(ArtifactVersionSchema)),
    create: oc
      .input(
        threadTarget.and(
          z.object({
            name: z.string().min(1).max(ARTIFACT_NAME_MAX_LENGTH),
            description: z.string().max(ARTIFACT_DESCRIPTION_MAX_LENGTH).optional(),
            mimeType: z.string().min(1),
            contentBase64: z.string().min(1),
            /** Unique per upload; a retry with the same nonce returns the artifact it created. */
            clientNonce: z.string().min(1).max(200).optional(),
          }),
        ),
      )
      .output(ArtifactSchema),
    get: oc.input(threadTarget.and(z.object({ artifactId: Id }))).output(ArtifactWithContentSchema),
    getById: oc.input(z.object({ artifactId: Id })).output(ArtifactWithContentSchema),
    remove: oc.input(z.object({ artifactId: Id })).output(z.object({ ok: z.literal(true) })),
  },
  usage: {
    list: oc.output(z.array(UsageRecordSchema)),
    summary: oc.output(UsageSummarySchema),
  },
  export: {
    bot: oc.input(botId).output(ExportManifestSchema),
    /**
     * A one-time token for `GET /api/account/export?token=`, which streams the account zip. Needs
     * a recent identity confirmation on this session (IDENTITY_CONFIRMATION_REQUIRED).
     */
    accountToken: oc.output(z.object({ token: z.string() })),
  },
  notifications: {
    registerPush: oc
      // No whitespace: the token store keeps the registering session on the next line.
      .input(z.object({ token: z.string().min(8).max(512).regex(/^\S+$/) }))
      .output(z.object({ ok: z.literal(true) })),
    unregisterPush: oc.output(z.object({ ok: z.literal(true) })),
  },
  search: {
    query: oc.input(z.object({ q: z.string().max(200) })).output(SearchQueryOutputSchema),
  },
  links: {
    /**
     * The site icon for a link's origin as a small data URL, resolved and cached by the server.
     * `retry` means the server was too busy to look; the origin may still have an icon.
     */
    favicon: oc
      // The longest origin: a scheme, a 253-character host name and a port.
      .input(z.object({ origin: z.string().max("https://".length + 253 + ":65535".length) }))
      .output(z.object({ icon: z.string().nullable(), retry: z.boolean().optional() })),
  },
  runs: {
    list: oc.input(z.object({ filter: z.enum(["active", "recent"]) })).output(RunsListOutputSchema),
  },
  /** The signed-in user's wallet; absent when the deployment offers none. */
  paymentWallet: {
    status: oc.output(PaymentWalletStatusSchema),
    /** Starts a sign-in the user finishes with the provider; `status` then reports it. */
    connect: oc.output(PaymentWalletStatusSchema),
    disconnect: oc.output(PaymentWalletStatusSchema),
  },
  voice: {
    catalog: oc.output(z.array(VoiceCatalogEntrySchema)),
    status: oc.input(settingsScopeInput.optional()).output(VoiceStatusSchema),
    credentials: oc.input(settingsScopeInput.optional()).output(z.array(VoiceCredentialSchema)),
    connect: oc
      .input(
        z
          .object({
            provider: z.string(),
            /** Optional for a self-hosted server and for providers without keys. */
            apiKey: z.string().max(4096).default(""),
            voiceId: z.string().max(120).optional(),
            speechModel: z.string().max(64).optional(),
            /** Self-hosted (OpenAI-compatible) server address and model ids. */
            baseUrl: z.string().max(2048).optional(),
            ttsModel: z.string().max(200).optional(),
            sttModel: z.string().max(200).optional(),
            /** Where the connection becomes the default voice. */
            scope: SettingsScopeSchema.optional(),
          })
          .superRefine((input, ctx) => {
            // Hosted keys are checked by the server, which knows which providers are keyless.
            if (input.provider === OPENAI_COMPATIBLE_PROVIDER_ID && !input.baseUrl?.trim()) {
              ctx.addIssue({ code: "custom", path: ["baseUrl"], message: "Enter a server URL." });
            }
          }),
      )
      .output(VoiceCredentialSchema),
    disconnect: oc
      .input(z.object({ provider: z.string().min(1) }))
      .output(z.object({ ok: z.literal(true) })),
    setVoice: oc
      .input(
        settingsScopeInput.extend({
          voiceId: z.string().min(1).max(120),
          provider: z.string().optional(),
        }),
      )
      .output(VoiceStatusSchema),
    setSpeechModel: oc
      .input(
        settingsScopeInput.extend({
          provider: z.string().min(1),
          speechModel: z.string().max(64),
        }),
      )
      .output(VoiceCredentialSchema),
    voices: oc
      .input(settingsScopeInput.extend({ provider: z.string().optional() }))
      .output(z.array(VoiceInfoSchema)),
    prepare: oc
      .input(
        z.object({
          text: z.string().max(20000),
          voiceId: z.string().max(120).optional(),
          botId: Id.optional(),
          scope: SettingsScopeSchema.optional(),
        }),
      )
      .output(z.object({ ready: z.boolean(), utterances: z.array(z.string()) })),
  },
  externalConversations: {
    updatePolicy: oc
      .input(UpdateExternalConversationPolicyInput)
      .output(ExternalConversationPolicySchema),
  },
  agentSecrets: {
    list: oc.output(z.array(AgentSecretSchema)),
    put: oc.input(AgentSecretInputSchema).output(AgentSecretSchema),
    remove: oc.input(z.object({ id: Id })).output(z.object({ ok: z.literal(true) })),
  },
  vault: {
    list: oc.input(settingsScopeInput.optional()).output(z.array(BotSecretMetadata)),
    put: oc.input(VaultPutInput).output(BotSecretMetadata),
    /** Needs a recent identity confirmation on this session (IDENTITY_CONFIRMATION_REQUIRED). */
    reveal: oc.input(VaultRevealInput).output(VaultRevealed),
    remove: oc
      .input(settingsScopeInput.extend({ name: StoredBotSecretName }))
      .output(z.object({ ok: z.literal(true) })),
  },
  /** The vault this Space uses, for apps that predate it. The bot only checks access. */
  botSecrets: {
    list: oc.input(z.object({ botId: Id })).output(z.array(BotSecretMetadata)),
    put: oc.input(BotSecretPutInput).output(BotSecretMetadata),
    remove: oc
      .input(z.object({ botId: Id, name: StoredBotSecretName }))
      .output(z.object({ ok: z.literal(true) })),
  },
  /**
   * Preá Kids: a family's child profiles, the family PIN, switching into a child's Preá and the
   * child's own session. Refusals are coded errors (plan_child_limit, pin_locked, controls_conflict,
   * ...); the UI renders them as docs/kids/ui-spec.md describes.
   */
  kids: {
    /** The family's plan room, PIN and timezone state, and its child profiles. */
    summary: oc.output(KidsSummarySchema),
    profile: {
      /** The first profile also sets the family PIN, which must be sent with it. */
      create: oc.input(ProfileCreateInputSchema).output(ChildProfileSchema),
      update: oc.input(ProfileUpdateInputSchema).output(ChildProfileSchema),
      /** Soft archive: hides the profile, revokes its child sessions and archives its Preá. */
      archive: oc.input(ProfileArchiveInputSchema).output(z.object({ ok: z.literal(true) })),
      /** Permanent delete. The name must be typed again; the Preá goes through bot removal. */
      delete: oc.input(ProfileDeleteInputSchema).output(z.object({ ok: z.literal(true) })),
    },
    controls: {
      get: oc.input(ControlsGetInputSchema).output(ChildControlsSchema),
      /** A write with a stale expectedVersion is refused with controls_conflict. */
      update: oc.input(ControlsUpdateInputSchema).output(ChildControlsSchema),
    },
    activity: {
      /** Full message text for 6–12; for 13–17 only times and counts, never message text. */
      list: oc.input(ActivityListInputSchema).output(ActivityListSchema),
      /** Deletes the child's whole conversation and the memories her Preá kept from it. The profile stays. */
      clear: oc.input(ActivityClearInputSchema).output(z.object({ ok: z.literal(true) })),
    },
    alerts: {
      list: oc.input(AlertsListInputSchema).output(z.object({ items: z.array(AlertSchema) })),
      acknowledge: oc.input(AlertAcknowledgeInputSchema).output(z.object({ ok: z.literal(true) })),
      /** Deletes one alert the parent has marked as read. An unread alert is refused with alert_not_acknowledged. */
      delete: oc.input(AlertDeleteInputSchema).output(z.object({ ok: z.literal(true) })),
    },
    report: {
      list: oc.input(ReportListInputSchema).output(ReportListSchema),
      get: oc.input(ReportGetInputSchema).output(ReportSummarySchema),
      /** Deletes one weekly report. The period is not rebuilt, so the sweep does not resend it. */
      delete: oc.input(ReportDeleteInputSchema).output(z.object({ ok: z.literal(true) })),
      /**
       * The report channel (art. 27–28): content, a bot reply or a problem goes to the internal review
       * queue. The child routes are on the child allowlist; the parent lists status and resolves.
       */
      channel: {
        child: {
          create: oc.input(ReportChannelCreateInputSchema).output(ReportChannelReceiptSchema),
        },
        parent: {
          create: oc.input(ReportChannelParentCreateInputSchema).output(ReportChannelReceiptSchema),
        },
        list: oc.input(ReportChannelListInputSchema).output(ReportChannelListSchema),
        resolve: oc.input(ReportChannelResolveInputSchema).output(ReportChannelItemSchema),
      },
      /** Contest of a blocked child turn (art. 27 of Decreto 12.880). Queues a review item only. */
      contest: {
        child: {
          create: oc.input(ContestInputSchema).output(ContestReceiptSchema),
        },
        parent: {
          create: oc.input(ContestParentInputSchema).output(ContestReceiptSchema),
        },
      },
    },
    pairing: {
      /** A six-digit code for the child's device, valid for five minutes and usable once. */
      create: oc.input(PairingCreateInputSchema).output(PairingCodeResultSchema),
      /** Called from the child's device without a session; rate limited by the server. */
      redeem: oc.input(PairingRedeemInputSchema).output(ChildSessionSchema),
    },
    pin: {
      set: oc.input(PinSetInputSchema).output(KidsPinResultSchema),
      verify: oc.input(PinVerifyInputSchema).output(KidsPinResultSchema),
      /** Needs the parent's account password; a right password clears a lockout. */
      reset: oc.input(PinResetInputSchema).output(KidsPinResultSchema),
    },
    session: {
      /** Called from the child's device without a session; the family PIN is the credential. */
      start: oc.input(SessionStartInputSchema).output(ChildSessionSchema),
      /** The child ends its session on this device after the family PIN is entered. */
      exitToParent: oc.input(SessionExitInputSchema).output(z.object({ ok: z.literal(true) })),
    },
    /** The child's own view. Only a live child session reaches this. */
    me: oc.output(ChildMeSchema),
    /**
     * The child's chat with her own Preá. Only a live child session reaches these; the server picks the
     * Preá from the session, so no request carries a bot id.
     */
    chat: {
      get: oc.output(ThreadSnapshotSchema),
      messages: oc
        .input(z.object({ before: z.number().int().nonnegative().optional() }))
        .output(ThreadMessagePageSchema),
      send: oc
        .input(
          z.object({
            text: z.string().trim().min(1).max(4000),
            clientNonce: z.string().min(1).max(200).optional(),
          }),
        )
        .output(threadSendOutput),
      stop: oc.output(z.object({ ok: z.literal(true) })),
    },
    /**
     * A photo of the child's homework. The judge reads it before anything keeps it; it is kept only while
     * the photo switch is on, and otherwise refused with status "disabled".
     */
    homework: {
      upload: oc.input(HomeworkUploadInputSchema).output(HomeworkResultSchema),
      /** The parent's list of kept photos for a profile: metadata only. */
      list: oc.input(HomeworkPhotoListInputSchema).output(HomeworkPhotoListSchema),
      /** The parent deletes one kept photo; its file goes with it. */
      delete: oc.input(HomeworkPhotoDeleteInputSchema).output(z.object({ ok: z.literal(true) })),
    },
  },
};

export type AppContract = typeof appContract;

type ProcedurePaths<Router, Prefix extends string = ""> = {
  [Key in keyof Router & string]: Router[Key] extends AnyContractProcedure
    ? `${Prefix}${Key}`
    : ProcedurePaths<Router[Key], `${Prefix}${Key}/`>;
}[keyof Router & string];

type AtPath<Tree, Path extends string> = Path extends `${infer Head}/${infer Rest}`
  ? Head extends keyof Tree
    ? AtPath<Tree[Head], Rest>
    : never
  : Path extends keyof Tree
    ? Tree[Path]
    : never;

/** A procedure's URL path under `/rpc/`, such as `bots/list`. */
export type RpcPath = ProcedurePaths<AppContract>;
type RpcInputs = { [Path in RpcPath]: AtPath<InferContractRouterInputs<AppContract>, Path> };
type RpcOutputs = { [Path in RpcPath]: AtPath<InferContractRouterOutputs<AppContract>, Path> };
export type RpcInput<Path extends RpcPath> = RpcInputs[Path];
export type RpcOutput<Path extends RpcPath> = RpcOutputs[Path];
